<script setup lang="ts">
/**
 * RowActionsMenu — kebab menu attached to each row in UsersTable.
 *
 * Composes the generic DropdownMenu primitive with IAM-specific
 * actions (Edit, Permissions, Reset password, Delete). Items are
 * filtered by the current admin's privileges so missing-permission
 * actions don't appear at all (rather than appearing disabled —
 * cleaner UX, matches how the sidebar gates routes).
 *
 * Required privileges:
 *   - Edit            → 'users'
 *   - Permissions     → 'user_group'
 *   - Reset password  → 'reset_user_password'
 *   - Delete          → 'delete_users'
 *
 * Caller emits the four discrete actions — UsersTable wires them
 * to its drawers and modals.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { MoreHorizontal, Pencil, Shield, KeyRound, Trash2 } from 'lucide-vue-next'
import DropdownMenu, {
  type DropdownMenuItemDef,
} from '@/components/ui/DropdownMenu.vue'
import Button from '@/components/ui/Button.vue'
import { useAuthStore } from '@/stores/auth'
import type { UserView } from '../api'

interface Props {
  user: UserView
}
const props = defineProps<Props>()

const emit = defineEmits<{
  edit: [user: UserView]
  permissions: [user: UserView]
  resetPassword: [user: UserView]
  delete: [user: UserView]
}>()

const auth = useAuthStore()
const { t } = useI18n()

const items = computed<DropdownMenuItemDef[]>(() => {
  const out: DropdownMenuItemDef[] = []

  if (auth.hasPrivilege('users')) {
    out.push({
      id: 'edit',
      label: t('actions.edit'),
      icon: Pencil,
      onClick: () => emit('edit', props.user),
    })
  }

  if (auth.hasPrivilege('user_group')) {
    out.push({
      id: 'permissions',
      label: t('actions.permissions'),
      icon: Shield,
      onClick: () => emit('permissions', props.user),
    })
  }

  if (auth.hasPrivilege('reset_user_password')) {
    out.push({
      id: 'resetPassword',
      label: t('actions.resetPassword'),
      icon: KeyRound,
      onClick: () => emit('resetPassword', props.user),
    })
  }

  if (auth.hasPrivilege('delete_users')) {
    out.push({
      id: 'delete',
      label: t('actions.delete'),
      icon: Trash2,
      kind: 'danger',
      separatorBefore: true,
      onClick: () => emit('delete', props.user),
    })
  }

  return out
})
</script>

<template>
  <DropdownMenu v-if="items.length > 0" :items="items">
    <template #trigger>
      <Button
        kind="ghost"
        size="sm"
        :icon="MoreHorizontal"
        :aria-label="$t('actions.menuLabel', { user: user.userName })"
      />
    </template>
  </DropdownMenu>
</template>
